import { useEffect, useMemo, useState } from 'react';
import { DollarSign, Package, CreditCard, ShoppingCart, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { supabase } from '@/lib/supabase';
import { SOURCE_LANGUAGES, TARGET_LANGUAGES, CEFR_LEVELS } from '@ellie/shared';

// ── Types ──────────────────────────────────────────────────────────────────────

interface OrderRow {
  amount_cents: number;
  status: string;
  created_at: string;
  subscription_id: string | null;
}

interface SubscriptionRow {
  status: string;
  created_at: string;
}

interface WorkbookProfileRow {
  native_language: string;
  target_language: string;
  level: string;
}

type DateRange = '30d' | '3m' | '12m' | 'all';

// Statuses that represent revenue (order was paid and progressed)
const REVENUE_STATUSES = ['paid', 'generating', 'review', 'approved', 'delivering', 'delivered'];

// ── Helpers ────────────────────────────────────────────────────────────────────

function getDateRangeStart(range: DateRange): string | null {
  if (range === 'all') return null;

  const now = new Date();
  switch (range) {
    case '30d':
      now.setDate(now.getDate() - 30);
      break;
    case '3m':
      now.setMonth(now.getMonth() - 3);
      break;
    case '12m':
      now.setMonth(now.getMonth() - 12);
      break;
  }
  return now.toISOString();
}

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function getLanguageName(code: string, type: 'source' | 'target'): string {
  if (type === 'source') {
    return SOURCE_LANGUAGES.find((l) => l.code === code)?.name ?? code.toUpperCase();
  }
  return TARGET_LANGUAGES.find((l) => l.code === code)?.name ?? code.toUpperCase();
}

function getLevelLabel(code: string): string {
  return CEFR_LEVELS.find((l) => l.code === code)?.name ?? code.toUpperCase();
}

/** Group orders into monthly buckets: { month: "Jan 2025", value: number } */
function groupByMonth<T extends { created_at: string }>(
  rows: T[],
  valueFn: (items: T[]) => number,
): { month: string; value: number }[] {
  const buckets = new Map<string, T[]>();

  for (const row of rows) {
    const d = new Date(row.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const arr = buckets.get(key) ?? [];
    arr.push(row);
    buckets.set(key, arr);
  }

  // Sort by key (YYYY-MM) and format for display
  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, items]) => {
      const [year, month] = key.split('-');
      const label = new Date(Number(year), Number(month) - 1).toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      });
      return { month: label, value: valueFn(items) };
    });
}

// ── Chart colors ───────────────────────────────────────────────────────────────

const CHART_BLUE = '#2C98D4'; // brand
const CHART_DARK = '#054C95'; // brand-dark
const PALETTE = [
  '#2C98D4',
  '#054C95',
  '#4AADE0',
  '#1B6B9A',
  '#7CC4E8',
  '#B1D4DE',
  '#0E3D6B',
  '#93D0F0',
];

// ── Subcomponents ──────────────────────────────────────────────────────────────

function KpiCard({
  icon,
  label,
  value,
  loading,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  loading: boolean;
}) {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-border bg-white p-5 shadow-sm">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-brand-light text-brand">
        {icon}
      </div>
      <div>
        {loading ? (
          <div className="mb-1 h-7 w-16 animate-pulse rounded bg-gray-200" />
        ) : (
          <p className="text-2xl font-bold text-text-primary">{value}</p>
        )}
        <p className="text-sm text-text-muted">{label}</p>
      </div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
      <h3 className="mb-4 text-sm font-semibold text-text-muted uppercase tracking-wide">
        {title}
      </h3>
      {children}
    </div>
  );
}

/** Horizontal bar chart for categorical data (language pairs, levels) */
function HorizontalBarChart({
  data,
  loading,
}: {
  data: { name: string; count: number }[];
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-6 animate-pulse rounded bg-gray-100" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return <p className="text-sm text-text-light">No data available</p>;
  }

  return (
    <ResponsiveContainer width="100%" height={data.length * 40 + 20}>
      <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
        <XAxis type="number" hide />
        <YAxis
          type="category"
          dataKey="name"
          width={140}
          tick={{ fontSize: 13, fill: '#6B6B6B' }}
          axisLine={false}
          tickLine={false}
        />
        <Tooltip
          formatter={(value) => [value, 'Count']}
          contentStyle={{ borderRadius: 8, border: '1px solid #E2DFD8' }}
        />
        <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={22}>
          {data.map((_, index) => (
            <Cell key={index} fill={PALETTE[index % PALETTE.length]} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Date range options ─────────────────────────────────────────────────────────

const DATE_RANGE_OPTIONS: { value: DateRange; label: string }[] = [
  { value: '30d', label: 'Last 30 days' },
  { value: '3m', label: 'Last 3 months' },
  { value: '12m', label: 'Last 12 months' },
  { value: 'all', label: 'All time' },
];

// ── Main component ─────────────────────────────────────────────────────────────

export function AnalyticsPage() {
  const [dateRange, setDateRange] = useState<DateRange>('12m');
  const [loading, setLoading] = useState(true);

  // Raw data
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionRow[]>([]);
  const [profiles, setProfiles] = useState<WorkbookProfileRow[]>([]);

  // ── Fetch data ─────────────────────────────────────────────────────────────

  useEffect(() => {
    async function fetchData() {
      setLoading(true);

      const rangeStart = getDateRangeStart(dateRange);

      // Build queries
      let ordersQuery = supabase
        .from('orders')
        .select('amount_cents, status, created_at, subscription_id');

      let subsQuery = supabase.from('subscriptions').select('status, created_at');

      // Date range filter (subscriptions and orders)
      if (rangeStart) {
        ordersQuery = ordersQuery.gte('created_at', rangeStart);
        subsQuery = subsQuery.gte('created_at', rangeStart);
      }

      // Profiles are always all-time (for popularity breakdowns)
      const profilesQuery = supabase
        .from('workbook_profiles')
        .select('native_language, target_language, level');

      const [ordersRes, subsRes, profilesRes] = await Promise.all([
        ordersQuery,
        subsQuery,
        profilesQuery,
      ]);

      setOrders((ordersRes.data as OrderRow[]) ?? []);
      setSubscriptions((subsRes.data as SubscriptionRow[]) ?? []);
      setProfiles((profilesRes.data as WorkbookProfileRow[]) ?? []);
      setLoading(false);
    }

    fetchData();
  }, [dateRange]);

  // ── Derived metrics ────────────────────────────────────────────────────────

  const revenueOrders = useMemo(
    () => orders.filter((o) => REVENUE_STATUSES.includes(o.status)),
    [orders],
  );

  const totalRevenue = useMemo(
    () => revenueOrders.reduce((sum, o) => sum + o.amount_cents, 0),
    [revenueOrders],
  );

  const totalOrders = orders.length;

  const avgOrderValue = useMemo(
    () => (revenueOrders.length > 0 ? totalRevenue / revenueOrders.length : 0),
    [totalRevenue, revenueOrders],
  );

  const conversionRate = useMemo(
    () => (totalOrders > 0 ? Math.round((revenueOrders.length / totalOrders) * 100) : 0),
    [revenueOrders, totalOrders],
  );

  // Subscriber breakdown
  const subsByStatus = useMemo(() => {
    const counts = { active: 0, trialing: 0, canceled: 0, past_due: 0 };
    for (const s of subscriptions) {
      if (s.status in counts) {
        counts[s.status as keyof typeof counts]++;
      }
    }
    return counts;
  }, [subscriptions]);

  const activeSubscribers = subsByStatus.active + subsByStatus.trialing;

  // Monthly revenue chart data
  const revenueChartData = useMemo(
    () =>
      groupByMonth(
        revenueOrders,
        (items) => items.reduce((sum, o) => sum + o.amount_cents, 0) / 100,
      ),
    [revenueOrders],
  );

  // Monthly order volume chart data
  const orderVolumeData = useMemo(() => groupByMonth(orders, (items) => items.length), [orders]);

  // Language pair popularity (all-time)
  const languagePairData = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of profiles) {
      const key = `${getLanguageName(p.native_language, 'source')} → ${getLanguageName(p.target_language, 'target')}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [profiles]);

  // Level distribution (all-time)
  const levelData = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of profiles) {
      const key = getLevelLabel(p.level);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    // Sort by CEFR order
    const levelOrder: string[] = CEFR_LEVELS.map((l) => l.name);
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => levelOrder.indexOf(a.name) - levelOrder.indexOf(b.name));
  }, [profiles]);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="font-heading text-2xl text-text-primary">Analytics</h1>
        <select
          value={dateRange}
          onChange={(e) => setDateRange(e.target.value as DateRange)}
          className="rounded-lg border border-border bg-white px-3 py-2 text-sm text-text-primary shadow-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
        >
          {DATE_RANGE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* KPI Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <KpiCard
          icon={<DollarSign size={22} />}
          label="Total Revenue"
          value={formatCurrency(totalRevenue)}
          loading={loading}
        />
        <KpiCard
          icon={<Package size={22} />}
          label="Total Orders"
          value={String(totalOrders)}
          loading={loading}
        />
        <KpiCard
          icon={<CreditCard size={22} />}
          label="Active Subscribers"
          value={String(activeSubscribers)}
          loading={loading}
        />
        <KpiCard
          icon={<ShoppingCart size={22} />}
          label="Avg. Order Value"
          value={formatCurrency(avgOrderValue)}
          loading={loading}
        />
        <KpiCard
          icon={<TrendingUp size={22} />}
          label="Conversion Rate"
          value={`${conversionRate}%`}
          loading={loading}
        />
      </div>

      {/* Revenue Over Time */}
      <div className="mb-6">
        <ChartCard title="Revenue Over Time">
          {loading ? (
            <div className="h-64 animate-pulse rounded bg-gray-100" />
          ) : revenueChartData.length === 0 ? (
            <p className="py-12 text-center text-sm text-text-light">
              No revenue data for this period
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={revenueChartData}
                margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
              >
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: '#6B6B6B' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6B6B6B' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v: number) => `€${v}`}
                />
                <Tooltip
                  formatter={(value) => [`€${Number(value).toFixed(0)}`, 'Revenue']}
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #E2DFD8',
                  }}
                />
                <Bar dataKey="value" fill={CHART_BLUE} radius={[4, 4, 0, 0]} barSize={32} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      {/* Order Volume + Subscriber Metrics */}
      <div className="mb-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title="Order Volume">
          {loading ? (
            <div className="h-56 animate-pulse rounded bg-gray-100" />
          ) : orderVolumeData.length === 0 ? (
            <p className="py-12 text-center text-sm text-text-light">No orders in this period</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={orderVolumeData} margin={{ top: 10, right: 10, left: 10, bottom: 0 }}>
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: '#6B6B6B' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6B6B6B' }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  formatter={(value) => [value, 'Orders']}
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #E2DFD8',
                  }}
                />
                <Bar dataKey="value" fill={CHART_DARK} radius={[4, 4, 0, 0]} barSize={28} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Subscriber Metrics">
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-gray-100" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border border-border p-4">
                <p className="text-2xl font-bold text-text-primary">{subsByStatus.active}</p>
                <p className="text-sm text-text-muted">Active</p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-2xl font-bold text-text-primary">{subsByStatus.trialing}</p>
                <p className="text-sm text-text-muted">Trialing</p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-2xl font-bold text-text-primary">{subsByStatus.canceled}</p>
                <p className="text-sm text-text-muted">Canceled</p>
              </div>
              <div className="rounded-lg border border-border p-4">
                <p className="text-2xl font-bold text-text-primary">{subsByStatus.past_due}</p>
                <p className="text-sm text-text-muted">Past Due</p>
              </div>
            </div>
          )}
        </ChartCard>
      </div>

      {/* Language Pairs + Level Distribution */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <ChartCard title="Language Pair Popularity">
          <HorizontalBarChart data={languagePairData} loading={loading} />
        </ChartCard>

        <ChartCard title="Level Distribution">
          <HorizontalBarChart data={levelData} loading={loading} />
        </ChartCard>
      </div>
    </div>
  );
}
